import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import '../Login/LoginPage.css';
import logo from '../images/image.png';
import colourlogo from '../images/colourlogo.png';
const LoginPage = ({ setIsLoggedIn }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const navigate = useNavigate();
  const { setUser, setSelectedCenter } = useUser();

  useEffect(() => {
    const rememberedUser = localStorage.getItem('remember-user');
    if (rememberedUser) {
      try {
        const { email, rememberMe: remembered } = JSON.parse(rememberedUser);
        if (email && remembered) {
          setFormData(prev => ({ ...prev, username: email }));
          setRememberMe(true);
        }
      } catch (e) {
        console.error('Failed to parse remembered user:', e);
        localStorage.removeItem('remember-user');
      }
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isFormValid = useMemo(() => {
    return formData.username.trim().length > 0 && formData.password.length > 0;
  }, [formData]);

  const handleInputChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    if (error) setError('');
  }, [error]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const clearLocalStorage = useCallback(() => {
    const keysToRemove = ['selectedAcademy', 'selectedCenter', 'auth-token'];
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (attemptCount >= 5) {
      setError('Too many failed attempts. Please try again in 15 minutes.');
      return;
    }

    if (!isFormValid) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);
    setError('');

    clearLocalStorage();
    setSelectedCenter(null);

    const loginPayload = {
      email: formData.username.trim(),
      password: formData.password,
      rememberMe
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); 

      const response = await fetch(process.env.REACT_APP_API_LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: JSON.stringify(loginPayload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned invalid response format');
      }

      const data = await response.json();

      if (!response.ok) {
        setAttemptCount(prev => prev + 1);

        const errorMessage = data.error || data.message || 'Login failed. Please check your credentials.';
        setError(errorMessage);
        return;
      }


      if (!data.userId || !data.email || !data.token) {
        setError('Invalid server response. Please try again.');
        return;
      }

      const userData = {
        userId: data.userId,
        email: data.email,
        privileges: data.privileges || [],
        userType: data.userType || 'user',
        academyIds: Array.isArray(data.academyIds) ? data.academyIds : [],
        academyId: data.academyIds?.length > 0 ? data.academyIds[0] : null,
        roles: Array.isArray(data.roles) ? data.roles : [],
      };

      setUser(userData);
      localStorage.setItem('auth-token', data.token);

      if (rememberMe) {
        localStorage.setItem('remember-user', JSON.stringify({
          email: userData.email,
          rememberMe: true
        }));
      } else {
        localStorage.removeItem('remember-user');
      }

      setAttemptCount(0);
      setIsLoggedIn(true);
      navigate('/', { replace: true });

    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timeout. Please check your connection and try again.');
      } else if (error.message.includes('Failed to fetch')) {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        console.error('Login error:', error);
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSupport = useCallback((e) => {
    e.preventDefault();
    window.open('mailto:hello@machaxi.com?subject=Login Support Request', '_blank');
  }, []);
  useEffect(() => {
    requestAnimationFrame(() => {
      const usernameEl = document.getElementById('username');
      const passwordEl = document.getElementById('password');
  
      setFormData({
        username: usernameEl?.value || '',
        password: passwordEl?.value || ''
      });
    });
  }, []);
  
  return (
    <div className="login-page-min-h-screen login-page-bg-gradient">
      {/* Main Content */}
      <div className="login-page-main-container">
        <div className="login-page-content-wrapper">
          {/* Left Section - Illustration (Hidden on mobile) */}
          {!isMobile && (
            <div className="login-page-left-section">
              <div className="login-page-illustration-container">
                <img
                  src={logo}
                  alt="Admin Dashboard Illustration"
                  className="login-page-illustration-img"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = '/placeholder-admin-illustration.svg';
                  }}
                />
                <div className="login-page-illustration-text">
                  <h2 className="login-page-illustration-title">Welcome Back</h2>
                  <p className="login-page-illustration-subtitle">Sign in to access your personalized dashboard</p>
                </div>
              </div>
            </div>
          )}

          {/* Right Section - Login Form */}
          <div className={`login-page-right-section ${isMobile ? 'mobile-full-width' : ''}`}>
            <div className="login-page-form-container">
              {/* Mobile-only logo */}
              {isMobile && (
                <div className="mobile-logo-container">
                  <img
                    src={colourlogo}
                    alt="Logo"
                    className="mobile-logo"
                    onError={(e) => {
                      e.target.src = '/placeholder-admin-illustration.svg';
                    }}
                  />
                </div>
              )}

              <div className="login-page-form-wrapper">
                <div className="login-page-form-header">
                  <h1 className="login-page-form-title">Sign In</h1>
                  <p className="login-page-form-subtitle">Enter your credentials to access the dashboard</p>
                </div>

                <form className="login-page-form" onSubmit={handleSubmit} noValidate>
                  {/* Username/Email Input */}
                  <div className="login-page-input-group">
                    <label htmlFor="username" className="login-page-input-label">
                      Username
                    </label>
                    <div className="login-page-input-container">
                      <span className="login-page-input-icon">
                        <span className="material-symbols-outlined">person</span>
                      </span>
                      <input
                        id="username"
                        type="text"
                        placeholder="Enter your username"
                        value={formData.username}
                        onChange={handleInputChange('username')}
                        className="login-page-input-field"
                        required
                        autoComplete="username"     
                        disabled={isLoading}
                      />
                    </div>

                  </div>

                  {/* Password Input */}
                  <div className="login-page-input-group">
                    <div className="login-page-password-header">
                      <label htmlFor="password" className="login-page-input-label">
                        Password
                      </label>
                    </div>
                    <div className="login-page-input-container">
                      <span className="login-page-input-icon">
                        <span className="material-symbols-outlined">lock</span>
                      </span>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={formData.password}
                        onInput={handleInputChange('password')}
                        className="login-page-input-field"
                        required
                        autoComplete="current-password"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="login-page-password-toggle"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        disabled={isLoading}
                      >
                        <span className="material-symbols-outlined">
                          {showPassword ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Remember Me Checkbox */}
                  <div className="login-page-checkbox-group">
                    <label className="login-page-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="login-page-checkbox"
                        disabled={isLoading}
                      />
                      <span className="login-page-checkbox-text">Remember me</span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <div className="login-page-button-container">
                    <button
                      type="submit"
                      className="login-page-submit-button"
                      disabled={isLoading}
                      aria-describedby={error ? 'login-error' : undefined}
                    >
                      <span className="login-page-button-content">
                        {isLoading ? (
                          <>
                            <span className="login-page-spinner"></span>
                            <span>Logging in...</span>
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined">login</span>
                            <span>Sign In</span>
                          </>
                        )}
                      </span>
                    </button>
                  </div>
                </form>

                {/* Error Message */}
                {error && (
                  <div id="login-error" className="login-page-error-message" role="alert">
                    <span className="material-symbols-outlined">error</span>
                    <div className="login-page-error-content">
                      <span>{error}</span>
                    </div>
                  </div>
                )}

                {/* Support Link */}

                <p className="login-page-support-text">
                  Having trouble logging in?{" "}
                  <button
                    type="button"
                    onClick={handleContactSupport}
                    className="login-page-support-link"
                    disabled={isLoading}
                  >
                    Contact Admin
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;